if (!customElements.get('ugc-video-reel')) {
  customElements.define(
    'ugc-video-reel',
    class UgcVideoReel extends HTMLElement {
      constructor() {
        super();

        this.modal = this.querySelector('[data-ugc-video-modal]');
        this.player = this.querySelector('[data-ugc-video-player]');
        this.closeButton = this.querySelector('[data-ugc-video-close]');
        this.scroller = this.querySelector('.ugc-video-reel__scroller');
        this.prevButton = this.querySelector('[data-ugc-scroll-prev]');
        this.nextButton = this.querySelector('[data-ugc-scroll-next]');
        this.nav = this.querySelector('[data-ugc-video-nav]');
        this.activeTrigger = null;
        this.isDragging = false;
        this.dragStarted = false;
        this.suppressClick = false;
        this.dragStartX = 0;
        this.dragStartScrollLeft = 0;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onModalClick = this.onModalClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onScrollerScroll = this.onScrollerScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.close = this.close.bind(this);
      }

      connectedCallback() {
        this.querySelectorAll('[data-ugc-video-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', this.onTriggerClick);
        });

        this.modal?.addEventListener('click', this.onModalClick);
        this.closeButton?.addEventListener('click', this.close);
        this.scroller?.addEventListener('pointerdown', this.onPointerDown);
        this.scroller?.addEventListener('pointermove', this.onPointerMove);
        this.scroller?.addEventListener('pointerup', this.onPointerUp);
        this.scroller?.addEventListener('pointercancel', this.onPointerUp);
        this.scroller?.addEventListener('pointerleave', this.onPointerUp);
        this.scroller?.addEventListener('scroll', this.onScrollerScroll, { passive: true });
        this.prevButton?.addEventListener('click', this.onPrevClick);
        this.nextButton?.addEventListener('click', this.onNextClick);
        window.addEventListener('resize', this.onResize);
        document.addEventListener('keyup', this.onKeyUp);

        if (this.scroller && window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.updateNavButtons());
          this.resizeObserver.observe(this.scroller);
        }

        this.updateNavButtons();
        requestAnimationFrame(() => this.updateNavButtons());
      }

      disconnectedCallback() {
        if (this.animationFrame) {
          cancelAnimationFrame(this.animationFrame);
        }

        this.querySelectorAll('[data-ugc-video-trigger]').forEach((trigger) => {
          trigger.removeEventListener('click', this.onTriggerClick);
        });

        this.modal?.removeEventListener('click', this.onModalClick);
        this.closeButton?.removeEventListener('click', this.close);
        this.scroller?.removeEventListener('pointerdown', this.onPointerDown);
        this.scroller?.removeEventListener('pointermove', this.onPointerMove);
        this.scroller?.removeEventListener('pointerup', this.onPointerUp);
        this.scroller?.removeEventListener('pointercancel', this.onPointerUp);
        this.scroller?.removeEventListener('pointerleave', this.onPointerUp);
        this.scroller?.removeEventListener('scroll', this.onScrollerScroll);
        this.prevButton?.removeEventListener('click', this.onPrevClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver?.disconnect();
        document.removeEventListener('keyup', this.onKeyUp);
        this.close();
      }

      onPrevClick() {
        this.scrollByDirection(-1);
      }

      onNextClick() {
        this.scrollByDirection(1);
      }

      onScrollerScroll() {
        this.updateNavButtons();
      }

      onResize() {
        this.updateNavButtons();
      }

      getScrollMetrics() {
        if (!this.scroller) return { step: 0, pageDistance: 0, visibleCount: 0 };

        const item = this.scroller.querySelector('.ugc-video-reel__item');
        if (!item) return { step: this.scroller.clientWidth, pageDistance: this.scroller.clientWidth, visibleCount: 1 };

        const styles = getComputedStyle(this.scroller);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        const itemWidth = item.getBoundingClientRect().width;
        const step = itemWidth + gap;
        const visibleCount = Math.max(1, Math.floor((this.scroller.clientWidth + gap) / step));
        const pageDistance = visibleCount * step;

        return { step, pageDistance, visibleCount };
      }

      scrollByDirection(direction) {
        if (!this.scroller) return;

        const metrics = this.getScrollMetrics();
        if (!metrics.step || !metrics.pageDistance) return;

        const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
        const rawTarget = this.scroller.scrollLeft + direction * metrics.pageDistance;
        const target =
          direction > 0 && rawTarget >= maxScroll - metrics.step * 0.5
            ? maxScroll
            : Math.max(0, Math.min(maxScroll, rawTarget));

        this.animateScrollTo(target, 1000);
      }

      animateScrollTo(target, duration) {
        if (!this.scroller) return;

        if (this.animationFrame) {
          cancelAnimationFrame(this.animationFrame);
        }

        const start = this.scroller.scrollLeft;
        const clampedTarget = Math.max(0, Math.min(this.scroller.scrollWidth - this.scroller.clientWidth, target));
        const startedAt = performance.now();

        this.scroller.classList.add('is-animating');

        const easeInOutCubic = (progress) =>
          progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        const tick = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          this.scroller.scrollLeft = start + (clampedTarget - start) * easeInOutCubic(progress);

          if (progress < 1) {
            this.animationFrame = requestAnimationFrame(tick);
          } else {
            this.scroller.scrollLeft = clampedTarget;
            this.animationFrame = null;

            requestAnimationFrame(() => {
              this.scroller?.classList.remove('is-animating');
              this.updateNavButtons();
            });
          }
        };

        this.animationFrame = requestAnimationFrame(tick);
      }

      updateNavButtons() {
        if (!this.scroller || (!this.prevButton && !this.nextButton)) return;

        const canScroll = this.scroller.scrollWidth > this.scroller.clientWidth + 2;
        const maxScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
        const atStart = this.scroller.scrollLeft <= 1;
        const atEnd = this.scroller.scrollLeft >= maxScroll - 1;

        if (this.prevButton) {
          this.prevButton.classList.toggle('is-hidden', !canScroll || atStart);
          this.prevButton.disabled = !canScroll || atStart;
        }

        if (this.nextButton) {
          this.nextButton.classList.toggle('is-hidden', !canScroll || atEnd);
          this.nextButton.disabled = !canScroll || atEnd;
        }
      }

      onTriggerClick(event) {
        if (this.suppressClick) {
          event.preventDefault();
          return;
        }

        const trigger = event.currentTarget;
        const template = this.querySelector(`[data-ugc-video-template="${trigger.dataset.ugcVideoTrigger}"]`);

        if (!template || !this.modal || !this.player) return;

        this.activeTrigger = trigger;
        this.player.replaceChildren(template.content.cloneNode(true));

        const video = this.player.querySelector('video');

        if (video) {
          video.controls = true;
          video.setAttribute('controlslist', 'nodownload');
          video.disablePictureInPicture = true;
          video.muted = false;
          video.autoplay = true;
          video.currentTime = 0;
        }

        this.modal.setAttribute('open', '');
        document.body.classList.add('ugc-video-reel-modal-open');

        if (video) {
          const playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise.catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }
        }

        this.closeButton?.focus();
      }

      onModalClick(event) {
        if (event.target === this.modal) {
          this.close();
        }
      }

      onKeyUp(event) {
        if (event.key === 'Escape' && this.modal?.hasAttribute('open')) {
          this.close();
        }
      }

      onPointerDown(event) {
        if (!this.scroller || event.pointerType === 'touch') return;

        this.isDragging = true;
        this.dragStarted = false;
        this.suppressClick = false;
        this.dragStartX = event.clientX;
        this.dragStartScrollLeft = this.scroller.scrollLeft;
      }

      onPointerMove(event) {
        if (!this.scroller || !this.isDragging) return;

        const dragDistance = event.clientX - this.dragStartX;

        if (Math.abs(dragDistance) > 5) {
          this.dragStarted = true;
          this.scroller.classList.add('is-dragging');
          this.scroller.scrollLeft = this.dragStartScrollLeft - dragDistance;
        }
      }

      onPointerUp(event) {
        if (!this.scroller || !this.isDragging) return;

        this.isDragging = false;
        this.scroller.classList.remove('is-dragging');
        this.suppressClick = this.dragStarted;

        if (this.scroller.hasPointerCapture(event.pointerId)) {
          this.scroller.releasePointerCapture(event.pointerId);
        }

        setTimeout(() => {
          this.dragStarted = false;
          this.suppressClick = false;
        }, 0);
      }

      close() {
        if (!this.modal || !this.player) return;

        const video = this.player.querySelector('video');

        if (video) {
          video.pause();
          video.removeAttribute('src');
          video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
          video.load();
        }

        this.player.replaceChildren();
        this.modal.removeAttribute('open');
        document.body.classList.remove('ugc-video-reel-modal-open');

        if (this.activeTrigger && document.contains(this.activeTrigger)) {
          this.activeTrigger.focus();
        }

        this.activeTrigger = null;
      }
    }
  );
}
